import { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import { OrderType, OwnersSortByType } from '@/shared/backend/types/leaderboard'

import { Button } from 'components/Button/Button'
import { IconClose } from 'components/Icons/IconClose'
import { IconFilterTrigger } from 'components/Icons/IconFilter'
import { Menu } from 'components/Menu/MenuWithPortal'
import { Typography } from 'components/Typography/Typography'
import { useOnClickOutsideMultiple } from 'utils/onClickOutside'
import { Option, ReactSelect } from 'components/ReactSelect/ReactSelect'
import { timeFilterOptions, walletsSortMobileOptions } from '../const'

const MenuTrigger = styled.div`
  display: flex;
`

const MenuContainer = styled.div`
  padding: 16px;
  width: 280px;
`

const Title = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 16px;
  border-bottom: 1px solid ${p => p.theme.colors.text.primary};
`

const Body = styled.div`
  margin-top: 16px;
  display: flex;
  justify-content: space-between;
`

const Actions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  gap: 16px;
`

interface Props {
  sortBy: OwnersSortByType
  initialTimeFilter: Option
  sortOrder: OrderType
  onSubmit: (newValue: Option, selectedSortOption: string) => void
}

export const WalletFilters = ({ initialTimeFilter, onSubmit, sortBy, sortOrder }: Props) => {
  const [isMenuOpen, setMenuOpen] = useState(false)
  const [selectedTimeFilter, setSelectedTimeFilter] = useState(initialTimeFilter)
  const [selectedSortOption, setSelectedSortOption] = useState(() => {
    const initialSortOption = walletsSortMobileOptions.find(o => o.value === `${sortBy} (${sortOrder})`)
    if (!initialSortOption) {
      return walletsSortMobileOptions[0]
    }
    return initialSortOption
  })

  useEffect(() => {
    setSelectedTimeFilter(initialTimeFilter)
  }, [initialTimeFilter])

  useEffect(() => {
    const initialSortOption = walletsSortMobileOptions.find(o => o.value === `${sortBy} (${sortOrder})`)
    setSelectedSortOption(initialSortOption || walletsSortMobileOptions[0])
  }, [sortBy, sortOrder])

  const menuTriggerRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const onClose = useCallback(() => setMenuOpen(false), [])

  const toggleMenu = useCallback(() => {
    setMenuOpen(!isMenuOpen)
    setSelectedTimeFilter({ ...selectedTimeFilter })
  }, [isMenuOpen, selectedTimeFilter])

  const handleSaveClick = useCallback(() => {
    setMenuOpen(!isMenuOpen)
    onSubmit(selectedTimeFilter, selectedSortOption.value)
  }, [isMenuOpen, selectedTimeFilter, selectedSortOption])

  const handleSelectTimeFilterChange = (newValue: Option) => {
    setSelectedTimeFilter(newValue)
  }

  const handleSortChange = (newValue: Option) => {
    setSelectedSortOption(newValue)
  }

  useOnClickOutsideMultiple([menuTriggerRef, menuRef], onClose)

  return (
    <div style={{ position: 'relative' }}>
      <MenuTrigger onClick={toggleMenu} ref={menuTriggerRef}>
        <IconFilterTrigger />
      </MenuTrigger>
      {isMenuOpen && (
        <Menu ref={menuRef} anchorElement={menuTriggerRef} style={{ marginLeft: -280, marginTop: -30 }}>
          <MenuContainer>
            <Title>
              <Typography variant="description" fontWeight="medium">
                Filters
              </Typography>
              <div onClick={toggleMenu} style={{ display: 'flex', cursor: 'pointer' }}>
                <IconClose />
              </div>
            </Title>
            <Body>
              <div style={{ width: 'calc(60% - 5px)' }}>
                <ReactSelect
                  selectedOption={selectedSortOption}
                  options={walletsSortMobileOptions}
                  onSelect={handleSortChange}
                />
              </div>
              <div style={{ width: 'calc(40% - 5px)' }}>
                <ReactSelect
                  options={timeFilterOptions}
                  selectedOption={selectedTimeFilter}
                  onSelect={handleSelectTimeFilterChange}
                />
              </div>
            </Body>
            <Actions>
              <Button outlined style={{ width: '50%', height: 36, padding: 0 }} onClick={toggleMenu}>
                Cancel
              </Button>
              <Button style={{ width: '50%', height: 36, padding: 0 }} onClick={handleSaveClick}>
                Submit
              </Button>
            </Actions>
          </MenuContainer>
        </Menu>
      )}
    </div>
  )
}
